import { TRANSCRIBABLE_EXTENSIONS } from './voice'

/**
 * Which files become project documents (board card 2u86) — the lists the
 * documents page picker, the documents upload route, the inbox's "save
 * attachment" and the DocumentPipeline agree on.
 *
 * The inbox's save uses `INBOX_DOCUMENT_EXTENSIONS` (no audio/video). The
 * chat composer's `/uploads` route deliberately uses the narrower
 * `CHAT_DOCUMENT_EXTENSIONS`: a picture dropped in chat stays a raw
 * `uploads/<uuid>-<name>` object (the viewImage id protocol), never a document.
 */

/** Office, PDF and text formats: converted to markdown (Firecrawl) or passed through. */
export const OFFICE_DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.csv',
  '.txt',
  '.md',
  '.markdown',
])

/**
 * Pictures kept as image documents. `.svg` is left out on purpose (scriptable,
 * and the model cannot look at it); `.avif`/`.bmp`/`.tiff` are not inputs the
 * Images binding takes on our plan.
 */
const IMAGE_MIME_TYPES: Readonly<Record<string, string>> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
}

export const IMAGE_DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set(Object.keys(IMAGE_MIME_TYPES))

/** Browsers cannot draw these; the pipeline writes a JPEG preview next to them. */
const PREVIEW_NEEDED_EXTENSIONS: ReadonlySet<string> = new Set(['.heic', '.heif'])

/** The cap of the Images binding's `.input()` — larger pictures are refused. */
export const MAX_IMAGE_DOCUMENT_BYTES = 20 * 1024 * 1024
/** Every other document upload. */
export const MAX_DOCUMENT_UPLOAD_BYTES = 50 * 1024 * 1024
/** An email attachment filed as a document (non-pictures; pictures use the image cap). */
export const MAX_SAVEABLE_ATTACHMENT_BYTES = 25 * 1024 * 1024

/** What the chat composer turns into documents: everything but pictures. */
export const CHAT_DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  ...OFFICE_DOCUMENT_EXTENSIONS,
  ...TRANSCRIBABLE_EXTENSIONS,
])

/** What the documents page and the inbox file as documents. */
export const DOCUMENT_UPLOAD_EXTENSIONS: ReadonlySet<string> = new Set([
  ...CHAT_DOCUMENT_EXTENSIONS,
  ...IMAGE_DOCUMENT_EXTENSIONS,
])

/**
 * What the inbox's "save attachment" files: office files, text and pictures —
 * never audio/video. Filing is rule-driven bulk work with nobody in the loop,
 * and transcription is the one per-minute conversion (board 2u86 review).
 */
export const INBOX_DOCUMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  ...OFFICE_DOCUMENT_EXTENSIONS,
  ...IMAGE_DOCUMENT_EXTENSIONS,
])

/** `.JPG` → `.jpg`; '' when the name has no extension (a leading dot is not one). */
export function fileExtensionOf(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot).toLowerCase() : ''
}

export function isImageDocumentExtension(ext: string): boolean {
  return IMAGE_DOCUMENT_EXTENSIONS.has(ext.toLowerCase())
}

/** The picture's type from its extension (mail clients often send octet-stream). */
export function imageMimeForExtension(ext: string): string | null {
  return IMAGE_MIME_TYPES[ext.toLowerCase()] ?? null
}

/** HEIC/HEIF: shown through the pipeline's `_processed/<id>.jpg` preview. */
export function imageNeedsPreview(ext: string): boolean {
  return PREVIEW_NEEDED_EXTENSIONS.has(ext.toLowerCase())
}

/** Size cap for one document upload of this type. */
export function maxDocumentBytes(ext: string): number {
  return isImageDocumentExtension(ext) ? MAX_IMAGE_DOCUMENT_BYTES : MAX_DOCUMENT_UPLOAD_BYTES
}
